import React, { useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import catchOnly from 'utils/catchOnly';

import useConsumerSearch, {
  ConsumerSearchError,
} from 'hooks/requests/consumerSearch';

import ReusableConsumerSearch from 'features/ReusableConsumerSearch';
import { NonClientError } from 'hooks/requests/fetchUtils';
import useQueryParameters from 'hooks/useQueryParameters';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import useAnalyticVariables from 'hooks/useAnalyticVariables';

import { useSelector } from 'react-redux';
import { selectTenant } from 'state/tenant';
import { RouteComponentProps } from 'react-router-dom';
import isPresent from 'utils/isPresent';
import { values as valuesOf, all, pick } from 'ramda';
import { selectCreditApplication } from 'state/creditApplication';

const prefillKeys = [
  'firstName',
  'lastName',
  'address1',
  'city',
  'state',
  'zip',
  'mobilePhone',
];

type PropTypes = RouteComponentProps;

const ConsumerSearch: React.FC<PropTypes> = props => {
  const [searchConsumer] = useConsumerSearch();

  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();
  const query = useQueryParameters();
  const { pathAfter } = useWorkflow();

  const {
    trustedBrandPrefill,
    overrideConfig,
    enabledFeatures: { requireAdditionalVerification },
  } = useSelector(selectTenant);
  const nextPath = useMemo(() => pathAfter(Step.CONSUMER_SEARCH), [pathAfter]);
  const { setAnalyticsVariables } = useAnalyticVariables();

  const creditApplication = useSelector(selectCreditApplication);

  const onSubmit = useCallback(
    async (values: {
      ssn: string;
      zip: string;
      mobilePhone?: string;
      otpCode?: string;
    }) => {
      try {
        const isSufficientPrefill = all(
          isPresent,
          valuesOf(pick(prefillKeys, creditApplication)),
        );
        if (isSufficientPrefill) {
          setAnalyticsVariables({ isSufficientPrefill: 'true' });
        }
        if (
          overrideConfig.phoneLookup &&
          trustedBrandPrefill &&
          isSufficientPrefill
        ) {
          push(nextPath);
          return;
        }

        const params = {
          ssn: values.ssn,
          zip: values.zip,
          mobilePhone: values.mobilePhone,
          carrier: query.carrier,
          keyword: query.keyword,
          shortCode: query.shortCode,
          token: query.token,
          otpCode: values.otpCode,
        };

        const { hasExistingAccount } = await searchConsumer(params);

        if (hasExistingAccount) {
          push('/existing-account');
        } else {
          push(nextPath);
        }
      } catch (e) {
        if (catchOnly(e, NonClientError, ConsumerSearchError)) {
          if (e instanceof NonClientError || e instanceof ConsumerSearchError) {
            if (
              requireAdditionalVerification &&
              requireAdditionalVerification.requirePrefill
            ) {
              push('/require-additional-verification');
            } else if (query.token && requireAdditionalVerification) {
              push('/require-additional-verification');
            } else {
              push(nextPath);
            }
          } else {
            enqueueSnackbar(
              'Something has gone wrong. Please try again later.',
              {
                variant: 'error',
                persist: true,
              },
            );
          }
        }
      }
    },
    [
      creditApplication,
      overrideConfig.phoneLookup,
      trustedBrandPrefill,
      query.carrier,
      query.keyword,
      query.shortCode,
      query.token,
      searchConsumer,
      setAnalyticsVariables,
      push,
      nextPath,
      requireAdditionalVerification,
      enqueueSnackbar,
    ],
  );

  return (
    <ReusableConsumerSearch
      lookupType={query.token ? 'tokenLookup' : 'phoneLookup'}
      onSubmit={onSubmit}
      skipToPath={nextPath}
      i18nPrefix="consumerSearch"
      {...props}
    />
  );
};

export default ConsumerSearch;
